/**
 * Tesoreria › Mastrini › Abbinamenti (12/10/2026): the accountant already
 * wrote, line by line, which invoice each payment settles («Pagamento Ft 123
 * 15/03/25», «Incasso Ft 7 6/02/24 …»). For every such line of a year the
 * invoice is looked for in the Tesoreria and the movement of the bank (or of
 * a card) with the same amount within a few days. The ledgers only propose:
 * a person confirms, and what was already reconciled by hand stays.
 */
import { z } from 'zod';
import { addDays } from './dates.js';
import { fromCents, toCents } from './money.js';
import { citedDates, textCitesNumber } from './reconciliation.js';
import { nameKey } from './tax-ids.js';
import { payableInvoices } from './treasury.js';
export const LEDGER_MATCH_STATES = ['pronto', 'da-guardare', 'senza-movimento', 'a-posto'];
export const LEDGER_MATCH_STATE_LABELS = {
    pronto: 'Pronti',
    'da-guardare': 'Da guardare',
    'senza-movimento': 'Senza movimento',
    'a-posto': 'Già a posto',
};
/** Days between the date of the ledger and the movement, either way. */
export const LEDGER_MATCH_DAYS = 10;
/** What a payment may exceed the residual by without being a problem: cents of rounding. */
const ROUNDING = 10;
/** A card spending may differ from the invoice for the exchange, up to 2%. */
const CARD_TOLERANCE = 0.02;
/** The lines of the ledgers that settle invoices: payments, collections, write-offs. */
const PAYMENT_CAUSAL = /^\s*(pagamento|pag\.|gc\s|incasso|abbuono)/i;
export const confirmLedgerMatchesSchema = z.object({
    keys: z.array(z.string().trim().min(1).max(4000)).min(1).max(2000),
});
/** The payment lines of an account: reductions of the balance with a causal of payment. */
function paymentLines(account, from) {
    const sign = account.cycle === 'attivo' ? 1 : -1;
    return account.entries.flatMap((entry) => {
        if (entry.opening || !entry.registrationDate || entry.registrationDate < from)
            return [];
        if (!PAYMENT_CAUSAL.test(entry.causal ?? ''))
            return [];
        const natural = sign * (toCents(entry.debit) - toCents(entry.credit));
        if (natural >= 0)
            return [];
        return [
            {
                entryId: entry.id,
                date: entry.registrationDate,
                causal: entry.causal,
                description: entry.description,
                amount: fromCents(-natural),
            },
        ];
    });
}
const isAbbuono = (line) => /^\s*abbuono/i.test(line.causal ?? '');
function daysBetween(a, b) {
    return Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000);
}
/** The words of a name that identify it in a bank description. */
function nameWords(name) {
    return nameKey(name)
        .replace(/[^a-z0-9 ]+/g, ' ')
        .split(' ')
        .filter((word) => word.length >= 4 && !['srls', 'italia', 'italy', 'limited'].includes(word));
}
/** The subsets of 2 to 4 invoices whose residuals make the amount: the first found, oldest first. */
function subsetOf(pool, amount) {
    const items = pool.slice(0, 14);
    const search = (start, chosen, sum) => {
        if (sum === amount && chosen.length >= 2)
            return chosen;
        if (chosen.length === 4 || sum >= amount)
            return null;
        for (let index = start; index < items.length; index += 1) {
            const found = search(index + 1, [...chosen, items[index]], sum + items[index].left);
            if (found)
                return found;
        }
        return null;
    };
    return search(0, [], 0);
}
/**
 * The proposals of a year of the ledgers. Lines of the same subject on the
 * same day are tried together first (one transfer for several invoices).
 * Each invoice is the one the line cites by number (the date written next to
 * it tells two of the same number apart), otherwise one or a few whose
 * residual makes the amount. The movement has the same amount within
 * LEDGER_MATCH_DAYS days and is not reconciled yet; the counterparty named in
 * the description and the nearest day choose among several.
 */
export function ledgerMatches(input) {
    const left = new Map();
    for (const invoice of input.invoices)
        left.set(invoice.id, toCents(invoice.residual));
    const claimed = new Set();
    const bankByAmount = new Map();
    for (const movement of input.bank) {
        const cents = toCents(movement.amount);
        bankByAmount.set(cents, [...(bankByAmount.get(cents) ?? []), movement]);
    }
    const invoicesOf = new Map();
    for (const invoice of input.invoices) {
        if (!invoice.subjectId || invoice.replaced || invoice.kind === 'nota_credito')
            continue;
        invoicesOf.set(invoice.subjectId, [...(invoicesOf.get(invoice.subjectId) ?? []), invoice]);
    }
    for (const list of invoicesOf.values())
        list.sort((a, b) => a.date.localeCompare(b.date));
    const work = input.accounts.flatMap((account) => {
        const lines = paymentLines(account, input.from);
        const byDay = new Map();
        for (const line of lines) {
            const day = `${isAbbuono(line) ? 'A' : 'P'}${line.date}`;
            byDay.set(day, [...(byDay.get(day) ?? []), line]);
        }
        return [...byDay.values()].map((group) => ({ account, group }));
    });
    work.sort((a, b) => a.group[0].date.localeCompare(b.group[0].date));
    const result = [];
    for (const { account, group } of work) {
        const together = group.length > 1 ? attempt(account, group, true) : null;
        if (together) {
            result.push(together);
            continue;
        }
        for (const line of group)
            result.push(attempt(account, [line], false));
    }
    return result.sort((a, b) => a.date.localeCompare(b.date) || a.accountName.localeCompare(b.accountName));
    /** One proposal for the lines; with `onlyWhole` null unless one movement pays them all. */
    function attempt(account, lines, onlyWhole) {
        const amount = lines.reduce((sum, line) => sum + toCents(line.amount), 0);
        const date = lines[0].date;
        const abbuono = isAbbuono(lines[0]);
        const movement = abbuono ? null : findMovement(account, amount, date);
        if (onlyWhole && !movement)
            return null;
        const base = {
            key: lines.map((line) => line.entryId).join('+'),
            cycle: account.cycle,
            accountId: account.id,
            accountName: account.name,
            subject: account.subject,
            date,
            kind: abbuono ? 'abbuono' : 'pagamento',
            lines,
            amount: fromCents(amount),
        };
        if (!account.subject) {
            return {
                ...base,
                state: 'da-guardare',
                problem: 'Soggetto dei mastrini da abbinare all’Anagrafica',
                invoices: [],
                movement: null,
                alternatives: 0,
                confirmable: false,
            };
        }
        const direction = account.cycle === 'attivo' ? 'emessa' : 'ricevuta';
        const pool = payableInvoices(invoicesOf.get(account.subject.id) ?? [], date, input.openingDate)
            .filter((invoice) => invoice.direction === direction)
            // A payment may come before its invoice (a pro forma, an advance), not months before.
            .filter((invoice) => invoice.date <= addDays(date, 60));
        const chosen = [];
        // Problems send the proposal to «Da guardare»; notes only explain it.
        const problems = [];
        const notes = [];
        let settledAlready = 0;
        let cited = 0;
        let missing = 0;
        for (const line of lines) {
            const text = ` ${[line.causal, line.description].filter(Boolean).join(' ')} `;
            const dates = new Set(citedDates(text));
            const byNumber = new Map();
            for (const invoice of pool) {
                if (!textCitesNumber(text, invoice.number))
                    continue;
                byNumber.set(invoice.number, [...(byNumber.get(invoice.number) ?? []), invoice]);
            }
            // Two invoices of the same number (another year): the date written, then one still open, then the latest.
            const named = [...byNumber.values()].map((same) => same.find((invoice) => dates.has(invoice.date)) ??
                same.filter((invoice) => (left.get(invoice.id) ?? 0) > 0 && invoice.date <= date).pop() ??
                same.filter((invoice) => invoice.date <= date).pop() ??
                same[0]);
            let rest = toCents(line.amount);
            if (named.length) {
                cited += 1;
                for (const invoice of named) {
                    const open = left.get(invoice.id) ?? 0;
                    if (open <= 0) {
                        settledAlready += 1;
                        continue;
                    }
                    const paid = Math.min(open, rest);
                    if (paid <= 0)
                        continue;
                    take(invoice, paid);
                    rest -= paid;
                }
                if (rest > 0 && rest < toCents(line.amount)) {
                    // «Ft 1 7/01/25-3 10/02/25»: an invoice written without «Ft», found by its residual.
                    const other = pool.find((invoice) => !named.includes(invoice) &&
                        (left.get(invoice.id) ?? 0) === rest &&
                        invoice.date <= addDays(date, 60));
                    if (other) {
                        take(other, rest);
                        notes.push(`Fattura ${other.number} scelta per il resto dell’importo`);
                        rest = 0;
                    }
                    else if (rest <= ROUNDING) {
                        notes.push(`Differenza di ${fromCents(rest)} sul residuo (arrotondamento)`);
                    }
                    else {
                        problems.push(`Importo oltre il residuo della fattura: restano ${fromCents(rest)}`);
                    }
                }
                if (rest > 0 && rest === toCents(line.amount) && settledAlready === 0)
                    missing += 1;
                continue;
            }
            // Not cited: the invoice whose residual is the amount, or a few that make it.
            const open = pool
                .map((invoice) => ({ invoice, id: invoice.id, left: left.get(invoice.id) ?? 0 }))
                .filter((item) => item.left > 0 && item.invoice.date <= addDays(date, 60));
            const same = open.filter((item) => item.left === rest);
            if (same.length) {
                take(same[0].invoice, rest);
                if (same.length === 1)
                    notes.push('Fattura non citata: scelta per importo');
                else
                    problems.push(`Fattura non citata: scelta per importo fra ${same.length}`);
                continue;
            }
            const subset = abbuono ? null : subsetOf(open, rest);
            if (subset) {
                for (const item of subset)
                    take(item.invoice, item.left);
                problems.push('Fatture non citate: scelte perché la somma dei residui è l’importo');
                continue;
            }
            missing += 1;
        }
        const advanced = chosen.some((invoice) => input.advanced.has(invoice.invoiceId));
        if (chosen.length === 0) {
            if (movement)
                claimed.delete(movement.found.id);
            if (onlyWhole)
                return null;
            return {
                ...base,
                state: settledAlready > 0 && missing === 0 ? 'a-posto' : 'da-guardare',
                problem: settledAlready > 0 && missing === 0
                    ? null
                    : abbuono
                        ? 'Abbuono: indica tu la fattura'
                        : cited
                            ? 'La fattura citata è già saldata in Tesoreria con altri pagamenti'
                            : 'Fattura non trovata in Tesoreria',
                invoices: [],
                movement: null,
                alternatives: 0,
                confirmable: false,
            };
        }
        if (missing > 0)
            problems.push('Una parte delle fatture citate non è in Tesoreria');
        if (movement && movement.alternatives > 0 && movement.tie) {
            problems.push(`${movement.alternatives + 1} movimenti possibili con lo stesso importo`);
        }
        if (advanced && !movement) {
            problems.push('Fattura anticipata dalla banca: guarda in «Anticipi fatture»');
        }
        if (abbuono)
            notes.push('Abbuono: chiude la fattura senza movimento');
        const state = problems.length
            ? 'da-guardare'
            : movement
                ? 'pronto'
                : 'senza-movimento';
        const told = [...problems, ...notes];
        return {
            ...base,
            state,
            problem: told.length ? told.join('; ') : null,
            invoices: chosen,
            movement: movement?.found ?? null,
            alternatives: movement?.alternatives ?? 0,
            confirmable: true,
        };
        function take(invoice, cents) {
            left.set(invoice.id, (left.get(invoice.id) ?? 0) - cents);
            const existing = chosen.find((item) => item.invoiceId === invoice.id);
            if (existing)
                existing.amount = fromCents(toCents(existing.amount) + cents);
            else {
                chosen.push({
                    invoiceId: invoice.id,
                    kind: invoice.kind,
                    number: invoice.number,
                    date: invoice.date,
                    amount: fromCents(cents),
                });
            }
        }
    }
    /** The movement of the bank, or of a card, that pays the amount; claimed when found. */
    function findMovement(account, cents, date) {
        const words = nameWords(account.subject?.name ?? account.name);
        const named = (text) => {
            const key = ` ${nameKey(text).replace(/[^a-z0-9 ]+/g, ' ')} `;
            return words.some((word) => key.includes(` ${word} `)) ? 0 : 1;
        };
        const signed = account.cycle === 'attivo' ? cents : -cents;
        const bank = (bankByAmount.get(signed) ?? [])
            .filter((movement) => !claimed.has(movement.id))
            .map((movement) => ({
            movement,
            days: Math.abs(daysBetween(movement.bookingDate, date)),
            named: named(`${movement.counterparty ?? ''} ${movement.description}`),
        }))
            .filter((item) => item.days <= LEDGER_MATCH_DAYS)
            .sort((a, b) => a.named - b.named || a.days - b.days);
        if (bank.length) {
            const [best, second] = bank;
            claimed.add(best.movement.id);
            return {
                found: {
                    source: 'banca',
                    id: best.movement.id,
                    accountCode: best.movement.accountCode,
                    label: best.movement.accountCode,
                    date: best.movement.bookingDate,
                    amount: best.movement.amount,
                    description: best.movement.description,
                },
                alternatives: bank.length - 1,
                tie: Boolean(second && second.named === best.named && second.days === best.days),
            };
        }
        if (account.cycle !== 'passivo' || !input.cards)
            return null;
        const cards = input.cards
            .filter((spending) => !claimed.has(spending.id))
            .map((spending) => ({
            spending,
            difference: Math.abs(toCents(spending.amount) - cents),
            days: Math.abs(daysBetween(spending.date, date)),
            named: named(spending.description),
        }))
            .filter((item) => item.days <= LEDGER_MATCH_DAYS &&
            item.difference <= Math.round(cents * CARD_TOLERANCE) &&
            // The invoice may be up to 2% more than the spending, not the other way round.
            toCents(item.spending.amount) * (1 + CARD_TOLERANCE) >= cents)
            .sort((a, b) => a.difference - b.difference || a.named - b.named || a.days - b.days);
        if (!cards.length)
            return null;
        const [best, second] = cards;
        claimed.add(best.spending.id);
        return {
            found: {
                source: 'carta',
                id: best.spending.id,
                accountCode: null,
                label: best.spending.label,
                date: best.spending.date,
                amount: best.spending.amount,
                description: best.spending.description,
            },
            alternatives: cards.length - 1,
            tie: Boolean(second &&
                second.difference === best.difference &&
                second.named === best.named &&
                second.days === best.days),
        };
    }
}
//# sourceMappingURL=ledger-matches.js.map